import React, {useContext, useEffect} from 'react';
import {Typography, Grid} from '@mui/material';
import {
  AutoFieldHelpers,
  useAutoField,
  useAutoForm,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {
  BuilderProps,
  getValidation,
} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {setUpAnnuityFields} from './builderAnnuityFields';
import {
  AnnuityIncomeFrequencyEnum,
  AnnuityPaymentTypeEnum,
  AnnuityRateTypeEnum,
  AnnuityTypeEnum,
} from '../../../schema-types/globalTypes';
import {
  transferFieldsType,
  transferSourceType,
} from '../BuilderTransfer/builderTransferFields';
import {POTS_PENSION} from '../../../utils/potsFilter';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {AnnuitySourceRow} from './AnnuitySourceRow';
import {AnnuityDestinationRow} from './AnnuityDestinationRow';
import {AccountContext} from '../../../contexts/AccountContext';
import {Pot} from '../../../schema-types/Pot';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {optionalRound} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {nullableBooleanToYesNoEmpty} from '../BuilderTransfer/builderNullableBooleanToYesNoEmpty';
import {getFeeFormikFromServer} from '../CreateBuilder/BuilderDAInitialFees';
import {
  defaultFeeFields,
  getPlanFeesFormikFromServer,
} from '../CreateBuilder/BuilderPlanFees';
import {UPDATES} from '../../../shared/UPDATES';

interface loadAnnuityProps {
  builderDetails: OvbuilderDetails_ovrecommendDetails | undefined | null;
  annuityHelpers: AutoFieldHelpers<setUpAnnuityFields | undefined>;
  transferSourcesFieldHelpers: AutoFieldHelpers<
    Record<string, transferSourceType> | undefined
  >;
  isSPPDA: boolean;
}

export const loadAnnuity = ({
  builderDetails,
  annuityHelpers,
  transferSourcesFieldHelpers,
  isSPPDA,
}: loadAnnuityProps) => {
  if (!builderDetails) {
    const annuityValues: setUpAnnuityFields = {
      annuityType: AnnuityTypeEnum.PERSONAL_PENSION_ANNUITY,
      incomeFrequency: AnnuityIncomeFrequencyEnum.MONTHLY,
      rateType: AnnuityRateTypeEnum.ORDINARY_RATES,
      paymentType: AnnuityPaymentTypeEnum.IN_ARREARS,
    };

    annuityHelpers.setValue(UPDATES._700, annuityValues);
    return;
  }

  const destinationPot = builderDetails?.pots?.find((p) => p?.planType === 'destination');

  const sourcePot = builderDetails?.pots?.find((p) => p?.planType === 'source');
  const sourceId = sourcePot?.pot?.id;

  const annuityValues: setUpAnnuityFields = {
    annuityType: destinationPot?.annuityType ?? undefined,
    incomeFrequency: destinationPot?.incomeFrequency ?? undefined,
    guaranteePeriod: destinationPot?.guaranteedPeriod ?? undefined,
    spouseAnnuityPercent: destinationPot?.spouseAnnuityPercent ?? undefined,
    rateType: destinationPot?.rateType ?? undefined,
    indexLinked: destinationPot?.indexLinked ?? undefined,
    longTermCareCapitalProtected: destinationPot?.ltcCapitalProtected ?? undefined,
    ltcProtectedPercent: destinationPot?.protectedPercentage ?? undefined,
    sourceId,
    paymentType: destinationPot?.paymentType ?? undefined,
    notes: builderDetails.note ?? undefined,
  };

  annuityHelpers.setValue(UPDATES._701, annuityValues);

  const sourceValues: Record<string, transferSourceType> = {
    [sourceId]: {
      id: sourceId,
      singleClientAmount: optionalRound(sourcePot?.singleClientAmount),
      full: sourcePot?.full || false,
      isAuthorityHeld: nullableBooleanToYesNoEmpty(sourcePot?.pot.isAuthorityHeld),
      isDataGather: nullableBooleanToYesNoEmpty(sourcePot?.pot.isDataGather),
      maxAmount: sourcePot?.pot.totalValue,
      contactId: sourcePot?.pot.contact?.id,
      checked: true,
      isShellPlan: false,
      planValue: sourcePot?.pot.currentDirectValue,
      inSpecie: sourcePot?.inSpecie ?? sourcePot?.pot?.inSpecie ?? undefined,
      initialFee: sourcePot?.initialFee?.id,
      legacyInitialFee: getFeeFormikFromServer(isSPPDA, sourcePot?.legacyInitialFee),
      planFees: getPlanFeesFormikFromServer(sourcePot) || defaultFeeFields,
    },
  };

  transferSourcesFieldHelpers.setValue(UPDATES._703, sourceValues);
};

export const BuilderAnnuityPlan: React.FC<BuilderProps> = ({
  sourceDestinationLabels,
  contactName,
}) => {
  const {pots} = useContext(AccountContext);
  const {setFieldValue} = useAutoForm<builderFieldsType>();
  const {builderDetails, loadingOvRecommend, builderReviewId, isSPPDA} =
    useBuilderContext();
  const {loaded} = useLoadedAutoField();
  const [annuityField, annuityMeta, annuityHelpers] = useAutoField<
    setUpAnnuityFields | undefined
  >('annuity');

  const changeSource = (event: React.ChangeEvent<any> | null, value: string) => {
    const id = value || event?.target?.value;
    annuityHelpers.setValue(UPDATES._702, {
      ...annuityField.value,
      sourceId: id,
    });
  };
  const [transferSourcesFieldObj, transferSourcesFieldMeta, transferSourcesFieldHelpers] =
    useAutoField<transferFieldsType['transferSources'] | undefined>(
      'transfer.transferSources'
    );
  // TODO: replace with initialValueOverrides
  // useEffect(() => {
  //   setFieldValue(UPDATES._704, 'transfer', {transferSources: {}})
  //     .then(() => {
  //       loadAnnuity({
  //         builderDetails,
  //         annuityHelpers,
  //         transferSourcesFieldHelpers,
  //         isSPPDA,
  //       });
  //     })
  //     .finally(() => {
  //       setLoaded();
  //     });
  // }, [builderDetails]);

  const filteredSourcePots = pots.filter(
    (p) => p.productName?.acronym?.toLocaleLowerCase() === POTS_PENSION && !p.parent
  );

  return (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={transferSourcesFieldMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {filteredSourcePots.map((el, i) => (
            <AnnuitySourceRow
              key={el.id}
              pot={el as unknown as BuilderPot}
              index={i}
              isSelected={annuityField.value?.sourceId === el.id}
              handleChange={() => changeSource(null, el.id)}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={annuityMeta.error}
          destinationLabel={sourceDestinationLabels?.second}
        />
        {loaded ? (
          <AnnuityDestinationRow validation={getValidation(annuityMeta)} />
        ) : (
          <Typography>Loading...</Typography>
        )}
      </Grid>
    </BuilderColumnWrapper>
  );
};
